<template>
  <div class="bitts-quick-filter">
    <BittsTooltip
      placement="bottomLeft"
      :show-tooltip="
        !isPopoverOpen &&
        !isOverNonRemovable &&
        ($slots['hover-content'] || disabledSummaryText)
          ? null
          : false
      "
      :mouse-enter-delay="0.4"
    >
      <template #title>
        <slot
          v-if="$slots['hover-content']"
          name="hover-content"
          :filter="filter"
        />
        <div
          v-else-if="disabledSummaryText"
          class="bitts-quick-filter__disabled-summary"
        >
          {{ disabledSummaryText }}
        </div>
      </template>
      <BittsPopover
        v-model:open="isPopoverOpen"
        placement="bottomLeft"
        :disabled="disabled"
      >
        <BittsFilterButton
          :prefix-icon="filter.faIcon"
          :size="size"
          variant="quick"
          :active="isSelected"
          :is-open="isPopoverOpen"
          :label-text="filter.label"
          :value-text="selectedText"
          :disabled="disabled"
          :removable="!filter.notRemovable"
          :is-negated="isNegated"
          @cleared:quick-filter="removeFilter"
          @entered-non-removable="isOverNonRemovable = true"
          @left-non-removable="isOverNonRemovable = false"
        />

        <template #content>
          <div class="bitts-quick-filter__popover-content">
            <!-- Header for non-text filters, or text filters with empty/contains operator -->
            <div
              v-if="
                showHeader &&
                (!isTextFilter || hasEmptyOperator || hasContainsOperator)
              "
              class="bitts-quick-filter__header"
              :class="{
                'border-b-0': hasEmptyOperator && showConvertToAdvanced,
              }"
            >
              <div class="bitts-quick-filter__header-top">
                <div class="bitts-quick-filter__header-title">
                  {{ filter.label }}
                  <BittsDropdownMenu
                    v-if="
                      showOperators &&
                      !!operators &&
                      (isTextFilter || filter.datatype === 'boolean')
                    "
                    v-model="selectedOperatorValue"
                    :options="operatorOptions"
                    :use-search="false"
                    mode="single"
                    variant="default"
                    :show-checkbox="false"
                    @selected:options="handleOperatorSelect"
                  >
                    <template #default="{ isOpen: opIsOpen }">
                      <button type="button">
                        <BittsTag :text="operatorLabel" size="x-small">
                          <template #post>
                            <FontAwesomeIcon
                              :icon="['fak', 'chevron-down']"
                              :class="['ml-4', { 'rotate-180': opIsOpen }]"
                            />
                          </template>
                        </BittsTag>
                      </button>
                    </template>
                  </BittsDropdownMenu>
                </div>
                <BittsButton
                  text="Clear"
                  variant="ghost"
                  type="neutral"
                  size="x-small"
                  @click="clearFilter"
                />
              </div>
            </div>
            <template v-if="hasContainsOperator">
              <div class="bitts-quick-filter__content p-8 max-w-[480px]">
                <BittsFilterTagInput
                  :model-value="tagFilterValues"
                  @update:model-value="(val) => (model = val)"
                  class="mx-8 mb-4"
                />
              </div>
              <div
                v-if="showConvertToAdvanced"
                class="bitts-quick-filter__footer"
              >
                <BittsOptionListItem
                  :option="{
                    label: 'Convert to advanced filter',
                    value: 'convert_to_advanced_filter',
                  }"
                  :fa-icon="['fak', 'cycle']"
                  @click="handleConvertToAdvanced"
                />
              </div>
            </template>
            <div
              v-else-if="hasEmptyOperator && showConvertToAdvanced"
              class="bitts-quick-filter__footer"
            >
              <BittsOptionListItem
                :option="{
                  label: 'Convert to advanced filter',
                  value: 'convert_to_advanced_filter',
                }"
                :fa-icon="['fak', 'cycle']"
                @click="handleConvertToAdvanced"
              />
            </div>
            <slot v-else-if="!hasEmptyOperator" name="content">
              <!-- Number/Datetime filter content -->
              <div
                v-if="!isTextFilter && !isRadioFilter"
                class="bitts-quick-filter__content"
              >
                <BittsSimpleNumberFilter
                  v-if="filter.datatype === 'number'"
                  :value="numberRangeValue"
                  :step="1"
                  :currency="null"
                  @range-changed="handleRangeChanged"
                />
                <BittsSimpleDateFilter
                  v-else-if="filter.datatype === 'datetime'"
                  :value="dateRangeValue"
                  @date-changed="handleRangeChanged"
                />
                <div
                  v-if="showConvertToAdvanced"
                  class="bitts-quick-filter__footer"
                >
                  <BittsOptionListItem
                    :option="{
                      label: 'Convert to advanced filter',
                      value: 'convert_to_advanced_filter',
                    }"
                    :fa-icon="['fak', 'cycle']"
                    @click="handleConvertToAdvanced"
                  />
                </div>
              </div>

              <!-- Radio filter content -->
              <template v-else-if="isRadioFilter">
                <div class="bitts-quick-filter__content">
                  <BittsSimpleRadioFilter
                    :value="radioFilterValue"
                    :options="booleanOptions.length ? booleanOptions : options"
                    :loading="false"
                    :searchable="
                      filter.datatype !== 'boolean' &&
                      filter.useSearch !== false
                    "
                    :search="search"
                    :has-default="filter.datatype !== 'boolean'"
                    @filter-changed="handleRadioChanged"
                    @search-applied="handleRadioSearch"
                    @tooltip-clicked="handleTooltipClicked"
                  />
                </div>
                <div
                  v-if="showConvertToAdvanced"
                  class="bitts-quick-filter__footer"
                >
                  <BittsOptionListItem
                    :option="{
                      label: 'Convert to advanced filter',
                      value: 'convert_to_advanced_filter',
                    }"
                    :fa-icon="['fak', 'cycle']"
                    @click="handleConvertToAdvanced"
                  />
                </div>
              </template>

              <!-- Text filter using BittsOptionList -->
              <template v-else>
                <BittsOptionList
                  v-model="model"
                  :options="searchedOptions"
                  :search-term="search"
                  :use-search="showSearch && filter.useSearch !== false"
                  :use-virtual-scroll="true"
                  :use-select-all="useSelectAll"
                  :virtual-scroll-max-height="240"
                  :virtual-scroll-item-height="30"
                  search-placeholder="Search"
                  mode="multi"
                  :show-checkbox="true"
                  variant="default"
                  @update:search-term="handleSearchUpdate"
                >
                  <template #search-top v-if="showOperators">
                    <div
                      class="bitts-quick-filter__header-top"
                      :class="{ 'mb-8': !hasEmptyOperator }"
                    >
                      <div class="bitts-quick-filter__header-title">
                        {{ filter.label }}
                        <BittsDropdownMenu
                          v-if="showOperators && !!operators"
                          v-model="selectedOperatorValue"
                          :options="operatorOptions"
                          :use-search="false"
                          mode="single"
                          :show-checkbox="false"
                          variant="default"
                          @selected:options="handleOperatorSelect"
                        >
                          <template #default="{ isOpen: opIsOpen }">
                            <button type="button">
                              <BittsTag :text="operatorLabel" size="x-small">
                                <template #post>
                                  <FontAwesomeIcon
                                    :icon="['fak', 'chevron-down']"
                                    :class="[
                                      'ml-4',
                                      { 'rotate-180': opIsOpen },
                                    ]"
                                  />
                                </template>
                              </BittsTag>
                            </button>
                          </template>
                        </BittsDropdownMenu>
                      </div>
                      <BittsButton
                        text="Clear"
                        variant="ghost"
                        type="neutral"
                        size="x-small"
                        @click="clearFilter"
                      />
                    </div>
                  </template>

                  <template #prefix="{ option }">
                    <slot name="option-prefix-icon" :item="option" />
                  </template>

                  <template #suffix="{ option }">
                    <slot name="option-suffix-icon" :item="option" />
                  </template>
                  <template
                    v-if="showConvertToAdvanced || showEmptyState || loading"
                    #footer
                  >
                    <div v-if="loading" class="bitts-quick-filter__loading">
                      <FontAwesomeIcon
                        :icon="['fak', 'loading']"
                        :spin="true"
                        class="text-neutral-accent w-28 h-28"
                      />
                    </div>
                    <div v-else-if="showEmptyState" class="py-8 text-center">
                      <BittsEmptyState
                        :title="
                          noCustomValue
                            ? 'No results found'
                            : 'No results found, create your own'
                        "
                        :description="
                          noCustomValue
                            ? 'Try a different search term'
                            : 'Type in the search bar to create values to filter by'
                        "
                        class="w-[280px] border-none"
                      />
                    </div>
                    <div
                      v-if="showConvertToAdvanced"
                      :class="{
                        'bitts-quick-filter__convert-wrapper': showEmptyState,
                      }"
                    >
                      <BittsOptionListItem
                        :option="{
                          label: 'Convert to advanced filter',
                          value: 'convert_to_advanced_filter',
                        }"
                        :fa-icon="['fak', 'cycle']"
                        @click="handleConvertToAdvanced"
                      />
                    </div>
                  </template>
                </BittsOptionList>
              </template>
            </slot>
          </div>
        </template>
      </BittsPopover>
    </BittsTooltip>
  </div>
</template>

<script setup lang="ts">
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { useDebounceFn } from '@vueuse/core';
import { intComma } from 'humanize-plus';
import { DateTime } from 'luxon';
import { computed, nextTick, onMounted, ref, watch } from 'vue';

import BittsButton from '../BittsButton/BittsButton.vue';
import BittsDropdownMenu from '../BittsDropdownMenu/BittsDropdownMenu.vue';
import BittsEmptyState from '../BittsEmptyState/BittsEmptyState.vue';
import BittsFilterButton from '../BittsFilterButton/BittsFilterButton.vue';
import BittsFilterTagInput from '../BittsFilterTagInput/BittsFilterTagInput.vue';
import BittsOptionList from '../BittsOptionList/BittsOptionList.vue';
import BittsOptionListItem from '../BittsOptionList/BittsOptionListItem.vue';
import BittsPopover from '../BittsPopover/BittsPopover.vue';
import BittsSimpleDateFilter from '../BittsSimpleFilter/BittsSimpleDateFilter/BittsSimpleDateFilter.vue';
import BittsSimpleNumberFilter from '../BittsSimpleFilter/BittsSimpleNumberFilter/BittsSimpleNumberFilter.vue';
import BittsSimpleRadioFilter from '../BittsSimpleFilter/BittsSimpleRadioFilter/BittsSimpleRadioFilter.vue';
import BittsTag from '../BittsTag/BittsTag.vue';
import BittsTooltip from '../BittsTooltip/BittsTooltip.vue';

import { FilterProp, Operator, SelectedRange } from './types';

type Option = FilterProp;

const {
  filter,
  options = [],
  selectedRange = [],
  operators = null,
  showOperators = false,
  operator = 'in',
  disabled = false,
  noCustomValue = false,
  openOnMount = true,
  showSearch = true,
  size = 'small',
  useSelectAll = false,
  showConvertToAdvanced = false,
  showHeader = true,
  selectedTextOverride = undefined,
  loading = false,
} = defineProps<{
  filter: FilterProp;
  selectedRange?: SelectedRange;
  options: Option[];
  operators?: Operator[] | null;
  showOperators?: boolean;
  operator?: string;
  disabled?: boolean;
  selectedTextOverride?: string;
  noCustomValue?: boolean;
  openOnMount?: boolean;
  showSearch?: boolean;
  size?: 'small' | 'medium';
  useSelectAll?: boolean;
  showConvertToAdvanced?: boolean;
  showHeader?: boolean;
  loading?: boolean;
}>();

const emit = defineEmits<{
  'remove-filter': [reload: boolean];
  'clear-filter': [];
  'apply-filter': [];
  search: [search: string, filterId: string | number];
  'filter-opened': [filterId: string | number];
  'update-range-value': [range: SelectedRange];
  'closed-filter': [filterId: string | number];
  'update-operator': [operator: Operator | null];
  'tooltip-clicked': [filterId: string | number];
  'convert-to-advanced': [];
}>();

const model = defineModel<(string | number)[] | Record<string, unknown> | null>(
  { default: () => [] },
);

const selectedIds = computed(() => {
  const val = model.value;

  if (Array.isArray(val)) {
    return val;
  }

  if (val && typeof val === 'object') {
    return Object.keys(val).filter(
      (key) => !!(val as Record<string, unknown>)[key],
    );
  }

  return [];
});

const radioFilterValue = computed(() => {
  const val = model.value;
  if (Array.isArray(val) && val.length > 0) {
    return val.map((v) => String(v)) as string[];
  }
  // Return empty array - BittsSimpleRadioFilter will default to first option ("All") when value is empty
  return [];
});

const search = ref('');
const searchedOptions = computed(() => {
  // IMPORTANT: If lookup or radio, we return empty so the default checkbox list doesn't conflict.
  // The Slot content will handle its own searching.
  if (filter.datatype === 'lookup' || isRadioFilter.value) return [];

  let filteredOptions: Option[] = [];

  if (filter.useSearch) {
    filteredOptions = options.filter((option) => {
      return option?.label.toLowerCase().includes(search.value.toLowerCase());
    });
  } else {
    filteredOptions = [...options];
  }

  if (!filter.preserveOrder) {
    filteredOptions.sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
    );
  }

  const customValueOptions: Option[] = [];
  if (
    !noCustomValue &&
    isTextFilter.value &&
    search.value.trim() &&
    !options.some(
      (opt) => opt.label.toLowerCase() === search.value.toLowerCase(),
    )
  ) {
    customValueOptions.push({
      value: search.value.trim(),
      label: search.value.trim(),
    });
  }

  // Persist previously selected custom values that aren't in the original options
  const optionValues = new Set(options.map((opt) => opt.value));
  const resultValues = new Set(filteredOptions.map((opt) => opt.value));
  const customValues = new Set(customValueOptions.map((opt) => opt.value));
  const selectedCustomValues = selectedIds.value
    .filter(
      (id) =>
        !optionValues.has(id) && !resultValues.has(id) && !customValues.has(id),
    )
    .filter((id) => {
      if (!search.value) return true;
      return String(id).toLowerCase().includes(search.value.toLowerCase());
    })
    .map((id) => ({ value: id, label: String(id) }));

  return [...customValueOptions, ...selectedCustomValues, ...filteredOptions];
});

const showEmptyState = computed(
  () =>
    !searchedOptions.value.length && !hasEmptyOperator.value && !search.value,
);

const isRadioFilter = computed(() => {
  return filter.datatype === 'radio' || filter.datatype === 'boolean';
});

const isTextFilter = computed(() => {
  return !['number', 'datetime', 'radio', 'boolean'].includes(
    filter.datatype || '',
  );
});

const booleanOptions = computed(() =>
  filter.datatype === 'boolean'
    ? [
        { label: 'true', value: 'true' },
        { label: 'false', value: 'false' },
      ]
    : [],
);

// Typed range values to avoid inline casts in template
const numberRangeValue = computed(() => selectedRange as [number, number]);

const dateRangeValue = computed(() => {
  if (!selectedRange || selectedRange.length !== 2) return [] as [];
  return selectedRange as [string, string];
});

const operatorLabel = computed<string>(() => {
  if (!operators?.length) return '';
  return operators.find((op) => op.value === operator)?.display || '';
});
const hasEmptyOperator = computed(() => operatorLabel.value.includes('empty'));
const isNegated = computed(
  () => typeof operator === 'string' && operator.includes('not'),
);
const hasContainsOperator = computed(
  () => operator === 'contains' || operator === 'does not contain',
);

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value);
}

const tagFilterValues = computed(() =>
  isStringArray(model.value) ? model.value : [],
);

// For glowup version: convert operators to options format for BittsDropdownMenu
const BOOLEAN_OPERATORS = new Set(['in', 'not in', 'is empty', 'is not empty']);

const operatorOptions = computed(() => {
  if (!operators) return [];
  const filtered =
    filter.datatype === 'boolean'
      ? operators.filter((op) => BOOLEAN_OPERATORS.has(op.value))
      : operators;
  return filtered.map((op) => ({
    label: op.display,
    value: op.value,
  }));
});

const selectedOperatorValue = ref<string | undefined>(operator);

function handleOperatorSelect(selectedOps: { value: string }[]) {
  const selectedOp = selectedOps[0];
  if (selectedOp) {
    const op = operators?.find((o) => o.value === selectedOp.value);
    emit('update-operator', op || null);
  }
}

const defaultState = JSON.stringify({
  options: [],
  range: [],
  operator: '',
});

const currentState = computed(() => {
  const opts = selectedIds.value.map((v) => String(v));
  opts.sort();
  const range = Array.isArray(selectedRange) ? selectedRange : [];
  const op = operator || '';
  return JSON.stringify({
    options: opts,
    range,
    operator: op,
  });
});

const baselineState = ref<string>(currentState.value);

function onOpenSetBaseline() {
  baselineState.value = currentState.value;
}

const isSelected = computed(() => {
  if (['number', 'datetime'].includes(filter.datatype || ''))
    return !!selectedRange && selectedRange.length > 0;
  if (isRadioFilter.value) {
    return (
      ['is empty', 'is not empty'].includes(operator as never) ||
      (selectedIds.value.length > 0 &&
        selectedIds.value[0] !== options[0]?.value)
    );
  }
  return (
    selectedIds.value.length > 0 ||
    ['is empty', 'is not empty'].includes(operator as never)
  );
});

const selectedText = computed(() => {
  if (selectedTextOverride !== undefined) return selectedTextOverride;
  if (!isSelected.value) return '';
  if (hasEmptyOperator.value) return operatorLabel.value;
  const prefix =
    (showOperators || filter.showOperatorInChip) && operatorLabel.value
      ? `${operatorLabel.value} `
      : '';
  if (hasContainsOperator.value && selectedIds.value.length > 0) {
    const val =
      selectedIds.value.length === 1
        ? String(selectedIds.value[0] ?? '')
        : `${selectedIds.value.length} values`;
    return `${prefix}${val}`;
  }
  if (filter.datatype === 'lookup' && selectedIds.value.length > 0) {
    return `${prefix}${selectedIds.value.length} selected`;
  }
  if (isRadioFilter.value && selectedIds.value.length > 0) {
    const optionPool = booleanOptions.value.length
      ? booleanOptions.value
      : options;
    const option = optionPool.find(
      (option) => String(option.value) === String(selectedIds.value[0]),
    );
    return `${prefix}${option?.label || ''}`;
  }
  if (filter.datatype === 'number' && numberRangeValue.value) {
    return numberRangeValue.value.map((n) => intComma(n)).join(' - ');
  }
  if (filter.datatype === 'datetime' && dateRangeValue.value[0]) {
    return dateRangeValue.value
      .map((d) => DateTime.fromISO(d).toFormat('MMM d, yyyy'))
      .join(' - ');
  }
  if (selectedIds.value.length > 1) {
    return `${prefix}${selectedIds.value.length} selected`;
  }
  const option = options.find(
    (option) => option.value === selectedIds.value[0],
  );
  return `${prefix}${option?.label || option?.toString() || ''}`;
});

const disabledSummaryText = computed((): string => {
  if (
    !disabled ||
    !isSelected.value ||
    hasEmptyOperator.value ||
    filter.datatype === 'number' ||
    filter.datatype === 'datetime' ||
    isRadioFilter.value
  ) {
    return '';
  }
  const summary = selectedIds.value
    .map(
      (id) =>
        options.find((o) => String(o.value) === String(id))?.label ??
        String(id),
    )
    .join(', ');
  return summary !== selectedText.value ? summary : '';
});

// Popover state
const isPopoverOpen = ref(false);
const isOverNonRemovable = ref(false);

watch(isPopoverOpen, (visible) => {
  onFilterVisibilityChanged(visible);
});

onMounted(() => {
  nextTick(() => {
    if (openOnMount && !isSelected.value) openDropdown();
    emit('search', '', filter.value);
  });
});

function handleRadioChanged(value: string[] | undefined) {
  if (filter.datatype === 'boolean' && value) {
    model.value = value.map((v) => v === 'true') as unknown as string[];
  } else {
    model.value = value || [];
  }
}

function handleRangeChanged(range: SelectedRange) {
  emit('update-range-value', range);
}

function handleSearchUpdate(value: string) {
  search.value = value;
}

function removeFilter() {
  emit('remove-filter', true);
}

function removeFilterWithoutReload() {
  emit('remove-filter', false);
}

function clearFilter() {
  if (!isSelected.value) return;
  baselineState.value = defaultState;
  emit('clear-filter');
}

function handleRadioSearch(query: string) {
  search.value = query;
  emit('search', query, filter.value);
}

function handleTooltipClicked() {
  emit('tooltip-clicked', filter.value);
}

function handleConvertToAdvanced() {
  isPopoverOpen.value = false;
  emit('convert-to-advanced');
}

const debouncedSearch = useDebounceFn(() => {
  emit('search', search.value, filter.value);
}, 300);

watch(search, () => {
  debouncedSearch();
});

function hasFilterChanged() {
  const oldState = JSON.parse(baselineState.value);
  const newState = JSON.parse(currentState.value);

  const oldOptions = new Set(oldState.options);
  const newOptions = new Set(newState.options);

  const optionsChanged =
    oldOptions.size !== newOptions.size ||
    [...oldOptions].some((opt) => !newOptions.has(opt));

  const rangeChanged =
    JSON.stringify(oldState.range) !== JSON.stringify(newState.range);

  const operatorChanged = oldState.operator !== newState.operator;

  return optionsChanged || rangeChanged || operatorChanged;
}

function onFilterVisibilityChanged(visible: boolean) {
  if (!visible) {
    search.value = '';
    emit('closed-filter', filter.value);

    const selected = isSelected.value;

    if (hasFilterChanged()) {
      if (!selected) removeFilter();
      emit('apply-filter');
    } else if (!selected) {
      removeFilterWithoutReload();
    }
  } else {
    onOpenSetBaseline();
    emit('filter-opened', filter.value);
  }
}

function openDropdown() {
  isPopoverOpen.value = true;
}

defineExpose({
  openDropdown,
});
</script>

<style scoped lang="pcss">
@reference "../../../../apps/crossbeam-app/src/css/theme.css";
.bitts-quick-filter {
  @apply flex items-center h-full;
}
.bitts-quick-filter__disabled-summary {
  @apply p-4 max-w-[280px] text-sm;
}
.bitts-quick-filter__popover-content {
  @apply min-w-[280px] rounded-8 overflow-hidden;
}
.bitts-quick-filter__content {
  @apply flex flex-col gap-4 max-h-[373px] overscroll-contain;
}
.bitts-quick-filter__header {
  @apply border-b border-neutral-border border-solid p-6 rounded-t-8 sticky top-0 bg-white z-10 flex flex-col gap-6 items-stretch;
}
.bitts-quick-filter__header-top {
  @apply flex items-center justify-between text-sm text-neutral-text;
}
.bitts-quick-filter__header-title {
  @apply flex items-center gap-4;
}
.bitts-quick-filter__option {
  @apply w-full min-w-0 flex items-center justify-between gap-8 px-8 py-2;

  &:hover {
    @apply bg-secondary-background-weak rounded-6;
  }
}
.bitts-quick-filter__option-checkbox {
  @apply flex items-center text-neutral-text-strong text-left;

  :deep(.ant-checkbox-wrapper) {
    @apply flex w-full;
  }

  :deep(.ant-checkbox-wrapper > span:last-child) {
    @apply flex-1 min-w-0 block;
  }
}
:deep(.bitts-tag) span {
  @apply h-[22px] flex items-center px-4;
}
:deep(.bitts-checkbox-with-label) {
  @apply w-full;
}

:deep(.bitts-simple-number-filter) {
  h2 {
    @apply hidden;
  }
}

.bitts-quick-filter__loading {
  @apply py-8 flex justify-center;
}
.bitts-quick-filter__footer {
  @apply border-t border-neutral-border px-[6px] py-4;
}
.bitts-quick-filter__convert-wrapper {
  @apply border-t border-neutral-border -mx-6 px-6 pt-6;
}
.bitts-quick-filter__content-scrollable-down {
  mask-image: linear-gradient(to top, transparent 0%, black 15%);
}
.bitts-quick-filter__content-scrollable-up {
  mask-image: linear-gradient(to bottom, transparent 0%, black 15%);
}

.bitts-quick-filter__content-scrollable-down.bitts-quick-filter__content-scrollable-up {
  mask-image: linear-gradient(to bottom, transparent 0%, black 15%),
    linear-gradient(to top, transparent 0%, black 15%);
  mask-composite: intersect;
  -webkit-mask-composite: source-in;
}
</style>
